import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { I18nProvider } from './i18n';
import BottomNav from './components/BottomNav';
import DesktopHeader from './components/DesktopHeader';
import AIAssistant from './components/AIAssistant';
import AuthModal from './components/AuthModal';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import OfficePage from './pages/OfficePage';
import CalendarPage from './pages/CalendarPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import StarProgramPage from './pages/StarProgramPage';
import ElitProgramPage from './pages/ElitProgramPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import FavoritesPage from './pages/FavoritesPage';
import NotificationsPage from './pages/NotificationsPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import PartnerDashboardPage from './pages/PartnerDashboard';
import OfficeCatalogPage from './pages/OfficeCatalogPage';
import OfficeProfilePage from './pages/OfficeProfilePage';
import OfficeHoursPage from './pages/OfficeHoursPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import CompanyOfficesPage from './pages/CompanyOfficesPage';
import OperatorsPage from './pages/OperatorsPage';
import { ServiceDetailPage, ProductDetailPage, EventDetailPage, CourseDetailPage, OrderDetailPage, BookingDetailPage, RentalDetailPage } from './pages/DetailPages';
import CommunityPage from './pages/CommunityPage';
import LocationPage from './pages/LocationPage';
import AdminPageNew from './pages/AdminPageNew';
import DailyPricingPage from './pages/DailyPricingPage';
import ShippingConfigPage from './pages/ShippingConfigPage';
import './styles.css';

// URL routing helpers
const getRouteFromPath = (pathname) => {
  const path = pathname.replace('/app', '').replace(/^\//, '') || 'explore';
  const parts = path.split('/');
  
  const routes = {
    'explore': { tab: 'explore', page: null },
    'search': { tab: 'search-results', page: null },
    'calendar': { tab: 'calendar', page: null },
    'orders': { tab: 'orders', page: null },
    'menu': { tab: 'profile', page: null },
    'community': { tab: null, page: 'community', params: { id: parts[1] || 'via-del-mare' } },
    'locations': { tab: null, page: 'location', params: { country: parts[1], region: parts[2], city: parts[3], category: parts[4] } },
    'localita': { tab: null, page: 'location', params: { country: parts[1], region: parts[2], city: parts[3], category: parts[4] } },
    'office': { tab: null, page: 'office', params: { id: parts[1] } }, // Accetta sia ID che slug
    'business': { tab: null, page: 'office', params: { id: parts[1] } }, // backward compat
    'star': { tab: null, page: 'star', params: {} },
    'elit': { tab: null, page: 'elit', params: {} },
    'cart': { tab: null, page: 'cart', params: {} },
    'checkout': { tab: null, page: 'checkout', params: {} },
    'order-success': { tab: null, page: 'order-success', params: { orderId: parts[1] } },
    'favorites': { tab: null, page: 'favorites', params: {} },
    'notifications': { tab: null, page: 'notifications', params: {} },
    'payments': { tab: null, page: 'payments', params: {} },
    'dashboard': { tab: 'dashboard', page: null },
    'orders-received': { tab: 'orders-received', page: null },
    'service': { tab: null, page: 'service-detail', params: { id: parseInt(parts[1]) || 1 } },
    'product': { tab: null, page: 'product-detail', params: { id: parseInt(parts[1]) || 1 } },
    'event': { tab: null, page: 'event-detail', params: { id: parseInt(parts[1]) || 1 } },
    'course': { tab: null, page: 'course-detail', params: { id: parseInt(parts[1]) || 1 } },
    'rental': { tab: null, page: 'rental-detail', params: { id: parseInt(parts[1]) || 1 } },
    'order': { tab: null, page: 'order-detail', params: { id: parts[1] } },
    'booking': { tab: null, page: 'booking-detail', params: { id: parts[1] } },
    'office-catalog': { tab: null, page: 'office-catalog', params: {} },
    'partner-catalog': { tab: null, page: 'office-catalog', params: {} }, // backward compat
    'office-operators': { tab: null, page: 'office-operators', params: {} },
    'partner-operators': { tab: null, page: 'office-operators', params: {} }, // backward compat
    'office-hours': { tab: null, page: 'office-hours', params: {} },
    'partner-hours': { tab: null, page: 'office-hours', params: {} }, // backward compat
    'office-profile': { tab: null, page: 'office-profile', params: {} },
    'business-profile': { tab: null, page: 'office-profile', params: {} }, // backward compat
    'business-hours': { tab: null, page: 'office-hours', params: {} }, // backward compat
    'company-settings': { tab: null, page: 'company-settings', params: {} },
    'company-offices': { tab: null, page: 'company-offices', params: {} },
    'daily-pricing': { tab: null, page: 'daily-pricing', params: {} },
    'shipping-config': { tab: null, page: 'shipping-config', params: {} },
    'admin': { tab: null, page: 'admin', params: {} },
    'admin-modules': { tab: null, page: 'admin-modules', params: {} },
  };
  
  return routes[parts[0]] || { tab: 'explore', page: null };
};

const getPathFromRoute = (tab, page, params = {}) => {
  if (page === 'community') return `/app/community/${params.id || 'via-del-mare'}`;
  if (page === 'location') {
    let path = `/app/locations/${params.country || 'italy'}`;
    if (params.region) path += `/${params.region}`;
    if (params.city) path += `/${params.city}`;
    if (params.category) path += `/${params.category}`;
    return path;
  }
  if (page === 'office') return `/app/office/${params.id || 1}`;
  if (page === 'business') return `/app/office/${params.id || 1}`; // backward compat
  if (page === 'star') return '/app/star';
  if (page === 'genius') return '/app/genius';
  if (page === 'elit') return '/app/elit';
  if (page === 'cart') return '/app/cart';
  if (page === 'checkout') return '/app/checkout';
  if (page === 'order-success') return `/app/order-success/${params.orderId || ''}`;
  if (page === 'favorites') return '/app/favorites';
  if (page === 'notifications') return '/app/notifications';
  if (page === 'payments') return '/app/payments';
  if (page === 'service-detail') return `/app/service/${params.id || 1}`;
  if (page === 'product-detail') return `/app/product/${params.id || 1}`;
  if (page === 'event-detail') return `/app/event/${params.id || 1}`;
  if (page === 'course-detail') return `/app/course/${params.id || 1}`;
  if (page === 'rental-detail') return `/app/rental/${params.id || 1}`;
  if (page === 'order-detail') return `/app/order/${params.id || ''}`;
  if (page === 'booking-detail') return `/app/booking/${params.id || ''}`;
  if (page === 'office-catalog') return '/app/office-catalog';
  if (page === 'partner-catalog') return '/app/office-catalog'; // backward compat
  if (page === 'office-operators') return '/app/office-operators';
  if (page === 'partner-operators') return '/app/office-operators'; // backward compat
  if (page === 'office-hours') return '/app/office-hours';
  if (page === 'partner-hours') return '/app/office-hours'; // backward compat
  if (page === 'office-profile') return '/app/office-profile';
  if (page === 'business-profile') return '/app/office-profile'; // backward compat
  if (page === 'business-hours') return '/app/office-hours'; // backward compat
  if (page === 'company-settings') return '/app/company-settings';
  if (page === 'company-offices') return '/app/company-offices';
  if (page === 'daily-pricing') return '/app/daily-pricing';
  if (page === 'shipping-config') return '/app/shipping-config';
  if (page === 'admin') return '/app/admin';
  
  if (tab === 'explore') return '/app/explore';
  if (tab === 'search-results') return '/app/search';
  if (tab === 'calendar') return '/app/calendar';
  if (tab === 'orders') return '/app/orders';
  if (tab === 'profile') return '/app/menu';
  if (tab === 'dashboard') return '/app/dashboard';
  if (tab === 'orders-received') return '/app/orders-received';
  
  return '/app';
};

const AppContent = () => {
  const { userMode } = useApp();
  const [activeTab, setActiveTab] = useState('explore');
  const [currentPage, setCurrentPage] = useState(null);
  const [pageParams, setPageParams] = useState({});
  const [scrollDirection, setScrollDirection] = useState('up');
  const lastScrollY = useRef(0);
  const isNavigating = useRef(false);

  // Initialize from URL on mount
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    const route = getRouteFromPath(window.location.pathname);
    if (route.page) {
      setCurrentPage(route.page);
      setPageParams(route.params || {});
    } else if (route.tab) {
      setActiveTab(route.tab);
    }
    // Mark as initialized after setting state
    setIsInitialized(true);
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const route = getRouteFromPath(window.location.pathname);
      isNavigating.current = true;
      if (route.page) {
        setCurrentPage(route.page);
        setPageParams(route.params || {});
      } else {
        setCurrentPage(null);
        setActiveTab(route.tab || 'explore');
      }
      setTimeout(() => { isNavigating.current = false; }, 100);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when state changes
  useEffect(() => {
    if (isNavigating.current || !isInitialized) return;
    const newPath = getPathFromRoute(activeTab, currentPage, pageParams);
    if (window.location.pathname !== newPath) {
      window.history.pushState({}, '', newPath);
    }
  }, [activeTab, currentPage, pageParams, isInitialized]);

  // Scroll direction detection
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollY.current ? 'down' : 'up';
      if (Math.abs(currentScrollY - lastScrollY.current) > 10) {
        setScrollDirection(direction);
        lastScrollY.current = currentScrollY;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset tab when mode changes (skip during initialization)
  const prevUserMode = useRef(userMode);
  useEffect(() => {
    // Only reset if userMode actually changed after initialization
    if (prevUserMode.current !== userMode && isInitialized) {
      setActiveTab(userMode === 'consumer' ? 'explore' : 'dashboard');
      setCurrentPage(null);
    }
    prevUserMode.current = userMode;
  }, [userMode, isInitialized]);

  const navigate = (page, params = {}) => {
    window.scrollTo(0, 0);
    if (page === 'search-results') {
      setActiveTab('search-results');
      setCurrentPage(null);
      setPageParams(params);
      return;
    }
    // Map shorthand names to detail pages
    const pageMap = {
      'service': 'service-detail',
      'product': 'product-detail',
      'event': 'event-detail',
      'course': 'course-detail',
    };
    const mappedPage = pageMap[page] || page;
    setCurrentPage(mappedPage);
    setPageParams(params);
  };

  const goBack = () => {
    window.history.back();
  };

  const handleTabChange = (tab) => {
    window.scrollTo(0, 0);
    setActiveTab(tab);
    setCurrentPage(null);
    setPageParams({});
  };

  // Pages without tab bar
  const pagesWithoutTabBar = [
    'community', 'location', 'office', 'star', 'elit', 'cart', 'checkout', 
    'order-success', 'favorites', 'notifications', 'payments',
    'service-detail', 'product-detail', 'event-detail', 'course-detail', 'rental-detail',
    'order-detail', 'booking-detail', 'office-catalog', 'office-operators',
    'office-profile', 'office-hours', 'company-settings', 'company-offices',
    'daily-pricing', 'shipping-config', 'admin', 'admin-modules'
  ];
  
  if (pagesWithoutTabBar.includes(currentPage)) {
    // Render page component directly without creating new function on each render
    let pageContent;
    switch(currentPage) {
      case 'community':
        pageContent = <CommunityPage communityId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'location':
        pageContent = <LocationPage 
          countrySlug={pageParams.country} 
          regionSlug={pageParams.region} 
          citySlug={pageParams.city}
          categorySlug={pageParams.category}
          onBack={goBack} 
          onNavigate={navigate} 
        />;
        break;
      case 'office':
        pageContent = <OfficePage officeId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'star':
        pageContent = <StarProgramPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'elit':
        pageContent = <ElitProgramPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'cart':
        pageContent = <CartPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'checkout':
        pageContent = <CheckoutPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'order-success':
        pageContent = <OrderSuccessPage orderId={pageParams.orderId} onNavigate={navigate} />;
        break;
      case 'favorites':
        pageContent = <FavoritesPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'notifications':
        pageContent = <NotificationsPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'payments':
        pageContent = <PaymentMethodsPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'service-detail':
        pageContent = <ServiceDetailPage serviceId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'product-detail':
        pageContent = <ProductDetailPage productId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'event-detail':
        pageContent = <EventDetailPage eventId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'course-detail':
        pageContent = <CourseDetailPage courseId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'rental-detail':
        pageContent = <RentalDetailPage rentalId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'order-detail':
        pageContent = <OrderDetailPage orderId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'booking-detail':
        pageContent = <BookingDetailPage bookingId={pageParams.id} onBack={goBack} onNavigate={navigate} />;
        break;
      case 'office-catalog':
        pageContent = <OfficeCatalogPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'office-operators':
        pageContent = <OperatorsPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'office-profile':
        pageContent = <OfficeProfilePage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'office-hours':
        pageContent = <OfficeHoursPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'company-settings':
        pageContent = <CompanySettingsPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'company-offices':
        pageContent = <CompanyOfficesPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'daily-pricing':
        pageContent = <DailyPricingPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'shipping-config':
        pageContent = <ShippingConfigPage onBack={goBack} onNavigate={navigate} />;
        break;
      case 'admin':
      case 'admin-modules':
        pageContent = <AdminPageNew onBack={goBack} onNavigate={navigate} />;
        break;
      default:
        pageContent = null;
    }

    // Check if we're in Admin page - render without Skipres header
    const isAdminPage = currentPage === 'admin' || currentPage === 'admin-modules';
    
    if (isAdminPage) {
      return (
        <div className="min-h-screen bg-gray-50">
          {pageContent}
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Desktop Header for inner pages too */}
        <DesktopHeader 
          activeTab={activeTab} 
          setActiveTab={handleTabChange}
          onNavigate={navigate}
          currentPage={currentPage}
        />
        {pageContent}
        <AIAssistant />
        <AuthModal />
      </div>
    );
  }

  // Consumer Mode Pages
  if (userMode === 'consumer') {
    const hideBottomNav = activeTab === 'search-results';
    
    // If consumer tries to access partner-only tabs, redirect to explore
    if (activeTab === 'dashboard') {
      setActiveTab('explore');
      return null;
    }
    
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Desktop Header */}
        <DesktopHeader 
          activeTab={activeTab} 
          setActiveTab={handleTabChange}
          onNavigate={navigate}
          currentPage={currentPage}
        />
        
        {/* Main Content */}
        <main className="lg:pb-0 pb-20">
          {activeTab === 'explore' && <HomePage onNavigate={navigate} />}
          {activeTab === 'search-results' && (
            <SearchPage 
              onNavigate={navigate} 
              onBack={() => handleTabChange('explore')}
              initialQuery={pageParams.query}
              initialCategory={pageParams.category}
              initialFilter={pageParams.filter}
              initialView={pageParams.view}
              initialLocation={pageParams.location}
              detectFilters={pageParams.detectFilters}
            />
          )}
          {activeTab === 'calendar' && <CalendarPage onNavigate={navigate} />}
          {activeTab === 'orders' && <OrdersPage onNavigate={navigate} />}
          {activeTab === 'profile' && <ProfilePage onNavigate={navigate} />}
          {activeTab === 'favorites' && <FavoritesPage onNavigate={navigate} onBack={() => handleTabChange('explore')} />}
          {activeTab === 'notifications' && <NotificationsPage onNavigate={navigate} onBack={() => handleTabChange('explore')} />}
        </main>
        
        <AIAssistant />
        <AuthModal />
        {!hideBottomNav && (
          <BottomNav activeTab={activeTab} setActiveTab={handleTabChange} scrollDirection={scrollDirection} onSearch={() => handleTabChange('search-results')} />
        )}
      </div>
    );
  }

  // Partner Mode Pages
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <DesktopHeader 
        activeTab={activeTab} 
        setActiveTab={handleTabChange}
        onNavigate={navigate}
        currentPage={currentPage}
      />
      
      {/* Main Content */}
      <main className="lg:pb-0 pb-20">
        {activeTab === 'dashboard' && <PartnerDashboardPage onNavigate={navigate} />}
        {activeTab === 'calendar' && <CalendarPage onNavigate={navigate} />}
        {activeTab === 'orders' && <OrdersPage onNavigate={navigate} />}
        {activeTab === 'profile' && <ProfilePage onNavigate={navigate} />}
      </main>
      
      <AIAssistant />
      <AuthModal />
      <BottomNav activeTab={activeTab} setActiveTab={handleTabChange} scrollDirection={scrollDirection} onSearch={() => handleTabChange('search-results')} />
    </div>
  );
};

const SkipresApp = () => {
  return (
    <AppProvider>
      <I18nProvider>
        <div className="skipres-app bg-white min-h-screen">
          <AppContent />
        </div>
      </I18nProvider>
    </AppProvider>
  );
};

export default SkipresApp;
